import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Mail, MapPin } from "lucide-react";
import { companyDetails } from "../data";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [company, setCompany] = useState(companyDetails);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const isHome = location.pathname === "/";

  useEffect(() => {
    fetch("https://api.dev.auxo.dk/public/content/virksomhedsoplysninger")
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (data && data.length > 0) {
          const item = data[0];
          setCompany({
            name: item.firmanavn?.da || item.firmanavn || companyDetails.name,
            cvr: item.cvr || companyDetails.cvr,
            address: item.adresse?.da || item.adresse || companyDetails.address,
            phone: item.telefon || companyDetails.phone,
            email: item.email || companyDetails.email,
            facebook: item.facebook_url || companyDetails.facebook,
            logo: (item.logo && item.logo[0]) || companyDetails.logo
          });
        }
      })
      .catch(() => {});
  }, []);

  // Check window width for 1200px breakpoint
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1200;
      setIsMobile(mobile);
      if (!mobile) {
        setIsOpen(false);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  const menuItems = [
    { label: "Forside", href: "/" },
    { label: "Priser", href: "/priser" },
    { label: "Holdstart", href: "/holdstart" },
    { label: "Materialer", href: "/materialer" },
    { label: "Nyheder", href: "/nyheder" },
    { label: "Om os", href: "/om-os" },
    { label: "Kontakt", href: "/kontakt" }
  ];

  const isActive = (href) => {
    if (href === "/") {
      return location.pathname === "/";
    }
    return location.pathname === href;
  };

  const isChildActive = (children) => {
    return children.some(child => location.pathname === child.href);
  };

  const desktopLinkBase = "px-4 py-2 rounded-md text-sm font-bold transition-colors";
  const desktopLinkActive = "bg-white/10 text-white";
  const desktopLinkIdle = "text-white/85 hover:text-white hover:bg-white/10";
  const mobileLinkActive = "text-[#00D492] bg-[#00D492]/10";
  const mobileLinkIdle = "text-white/50 hover:text-white hover:bg-white/10";

  return (
    <header className="w-full absolute left-0 top-0 z-50 border-b border-white/10 bg-transparent shadow-none">
      {/* Top Bar with Contact Info */}
      {!isHome && (
      <div className="bg-slate-900/40 backdrop-blur-sm text-white py-2 px-4 text-xs border-b border-white/5">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 sm:flex-row">
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://goo.gl/maps/KtPsDZAXJ2ui8hZK8" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-[#00D492] transition-colors">
              <MapPin className="w-3.5 h-3.5 text-[#00D492]" />
              <span className="whitespace-nowrap">{company.address}</span>
            </a>
            <a href={`tel:${company.phone.replace(/\s+/g, "")}`} className="flex items-center gap-1.5 hover:text-[#00D492] transition-colors">
              <Phone className="w-3.5 h-3.5 text-[#00D492]" />
              <span className="whitespace-nowrap">{company.phone}</span>
            </a>
            <a href={`mailto:${company.email}`} className="flex items-center gap-1.5 hover:text-[#00D492] transition-colors">
              <Mail className="w-3.5 h-3.5 text-[#00D492]" />
              <span className="whitespace-nowrap">{company.email}</span>
            </a>
          </div>
          <div className="text-slate-400 text-[11px]">
            CVR: {company.cvr}
          </div>
        </div>
      </div>
      )}

      {/* Main Navigation */}
      <div className={`mx-auto flex max-w-7xl items-center justify-between px-4 py-4 md:px-6 ${isHome ? "pt-6" : ""}`}>
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src="https://assets.auxodev.dk/assets/354bdcac-d302-42dd-b3d6-dafad7605e7e/225a8a96-ecd5-4478-8aa7-f0833a9ec198.png"
            alt="Køreskolen AG Logo"
            className="h-12 w-auto object-contain transition-transform group-hover:scale-105"
          />
        </Link>

        {/* Desktop Menu (>= 1200px) */}
        {!isMobile && (
          <nav className="flex items-center gap-1">
            {menuItems.map((item) => {
              if (item.children) {
                const active = isChildActive(item.children);
                return (
                  <div key={item.label} className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className={`flex items-center gap-1 px-4 py-2 rounded-md text-sm font-bold transition-colors cursor-pointer ${
                        active ? desktopLinkActive : desktopLinkIdle
                      }`}
                    >
                      Services
                      <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
                    </button>

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-48 rounded-lg border border-slate-800 bg-black py-1 shadow-lg z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            onClick={() => setIsDropdownOpen(false)}
                            className={`block px-4 py-2.5 text-sm font-semibold transition-colors ${
                              isActive(child.href) ? desktopLinkActive : desktopLinkIdle
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`${desktopLinkBase} ${isActive(item.href) ? desktopLinkActive : desktopLinkIdle}`}
                >
                  {item.label}
                </Link>
              );
            })}

          </nav>
        )}

        {/* Mobile Menu Button (< 1200px) */}
        {isMobile && (
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-2 rounded-md transition-colors cursor-pointer text-white hover:bg-white/10"
            aria-label="Åbn menu"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        )}
      </div>

      {/* Mobile Menu Drawer (< 1200px) */}
      {isMobile && isOpen && (
        <div className="w-full border-t border-white/10 py-4 px-4 shadow-inner animate-in slide-in-from-top duration-200 bg-black text-white">
          <nav className="flex flex-col gap-1">
            {menuItems.map((item) => {
              if (item.children) {
                const active = isChildActive(item.children);
                return (
                  <div key={item.label} className="flex flex-col">
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className={`flex items-center justify-between px-4 py-3 rounded-md text-base font-bold transition-colors cursor-pointer ${
                        active ? mobileLinkActive : mobileLinkIdle
                      }`}
                    >
                      {item.label}
                      <ChevronDown className={`w-5 h-5 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
                    </button>

                    {isDropdownOpen && (
                      <div className={`ml-4 mt-1 mb-2 flex flex-col gap-1 border-l-2 pl-6 ${isHome ? "border-white/10" : "border-[#00D492]/10"}`}>
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            onClick={() => setIsOpen(false)}
                            className={`px-4 py-2.5 rounded-md text-sm font-semibold transition-colors ${
                              isActive(child.href) ? mobileLinkActive : mobileLinkIdle
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className={`px-4 py-3 rounded-md text-base font-bold transition-colors ${
                    isActive(item.href) ? mobileLinkActive : mobileLinkIdle
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}

            {/* Mobile CTA */}
            <Link
              to="/holdstart"
              onClick={() => setIsOpen(false)}
              className="mt-4 w-full py-3 font-bold text-center rounded-md transition-colors cursor-pointer bg-[#00D492] text-white shadow-md hover:bg-[#00bf80]"
            >
              Tilmeld Hold
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
